import { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { cx, getDownloadURL } from '@/utils';
import DotButton from '@common/DotButton';
import { useDotButton } from '@common/DotButton/DotButton';
import { PageNewsletterBlock, type PageLink } from '@common/Providers/GraphqlProvider/generated';
import { t } from '@lingui/macro';
import { TitleWrapper } from '@views/Home/components';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';

import { ROUTES_CLIENT } from '@/utils/constants';
import SvgCalendar from '@/components/common/Icons/Calendar';
import { getPlainTextFromEditorState } from '@/components/common/LexicalEditorComponent';
import { LexicalViewer } from '@/components/common/LexicalEditorComponent/LexicalViewer';
import { Typography } from '@/components/ui/Typography';
import { splitTitle } from '@/components/views/Home/components/TitleWrapper';

import { useNewsItemsFiltered } from './hooks/useNewsItemsFiltered';
import styles from './styles.module.css';

// Component for handling image with fallback
const ImageWithFallback = ({
  src,
  alt,
  className,
  fill,
  sizes,
}: {
  src: string;
  alt: string;
  className?: string;
  fill?: boolean;
  sizes?: string;
}) => {
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div
        className={`${className} absolute inset-0 flex h-full w-full items-center justify-center bg-gray-200`}
      >
        <div className="flex flex-col items-center justify-center p-4 text-gray-400">
          <svg
            className="mb-2 h-8 w-8"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-center text-xs">Imagen no disponible</span>
        </div>
      </div>
    );
  }

  return (
    <Image
      className={className}
      src={src}
      alt={alt}
      fill={fill}
      sizes={sizes}
      onError={() => setImageError(true)}
    />
  );
};

// Helper function to safely extract text from summary field
const getSummaryText = (summary: string | null | undefined): string => {
  if (!summary) return '';

  try {
    // Try to parse as JSON first (Lexical editor state)
    JSON.parse(summary);

    return getPlainTextFromEditorState(summary);
  } catch {
    // If not JSON, treat as plain text
    return summary;
  }
};

type TNews = {
  __typename: string;
  id: string;
  link: string;
  updatedAt: string;
  publicationDate: string;
  image: {
    __typename: string;
    id: string;
    url: string;
    fileName: string;
    fileMongoId: string;
  };
  title: string;
  summary: string;
  linkItem: PageLink;
  seo?: {
    metadata?: Array<{
      slug?: string;
    }>;
  };
}[][];

const News = ({
  currentPageNewsletterBlock,
}: {
  currentPageNewsletterBlock?: PageNewsletterBlock;
}) => {
  const {
    home: { newsletterBlock: newsletterBlockContext },
  } = useHomeContext();

  const router = useRouter();

  const newsletterBlock = currentPageNewsletterBlock || newsletterBlockContext;

  // Use the new hook to fetch news items
  const {
    data: newsItems,
    isLoading,
    error,
  } = useNewsItemsFiltered({
    newsletterBlock,
    enabled: !newsletterBlock?.isDisabled,
  });

  if (newsletterBlock?.isDisabled) return null;

  // Show loading state
  if (isLoading) {
    return (
      <section className={'bg-concrete pb-[40px] pt-[28px]'}>
        <div className={'inner-extend'}>
          <TitleWrapper
            title1={'Entérate'}
            action={{
              text: 'Ver más',
              onClick: () => router.push('/press-room'),
            }}
            title2={'de las últimas noticias'}
          />
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state or no content
  if (error || !newsItems || newsItems.length === 0) {
    return null;
  }

  const [title1, title2] = splitTitle(newsletterBlock?.newsletterTitle, {
    show: newsletterBlock?.showTitle,
    defaultTitle1: 'Entérate',
    defaultTitle2: 'de las últimas noticias',
  });

  return (
    <section className={'bg-concrete pb-[40px] pt-[28px]'}>
      <div className={'inner-extend'}>
        <TitleWrapper
          title1={title1}
          action={{
            text: 'Ver más',
            onClick: () => router.push('/press-room'),
          }}
          title2={title2}
          description={newsletterBlock?.newsletterDescription}
        />
        <div>
          <SlideDesktop currentPageNewsletterBlock={newsletterBlock} newsItems={newsItems} />
        </div>
      </div>
    </section>
  );
};

const SlideDesktop = ({
  currentPageNewsletterBlock,
  newsItems,
}: {
  currentPageNewsletterBlock?: PageNewsletterBlock;
  newsItems: any[];
}) => {
  const {
    home: { newsletterBlock: newsletterBlockContext },
  } = useHomeContext();

  const newsletterBlock = currentPageNewsletterBlock || newsletterBlockContext;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  const news: TNews = useMemo(() => {
    const newArray: any[] = [];

    // Use the newsItems prop instead of newsletterBlock.news
    if (newsItems && newsItems.length > 0) {
      for (let i = 0; i < newsItems.length; i += 3) {
        const group = newsItems.slice(i, i + 3);
        newArray.push(group);
      }
    }
    return newArray;
  }, [newsItems]);

  const Content = (
    <div className="flex">
      <NewsGroup group={news[0]} className="" />
      {news?.map((_new, key) => {
        if (key === 0) return null;
        return <NewsGroup key={key} group={_new} className="hidden md:block" />;
      })}
    </div>
  );

  return (
    <>
      <div className="hidden overflow-hidden md:block" ref={emblaRef}>
        {Content}
        <div className={'mt-8 hidden justify-center space-x-2 md:flex'}>
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={cx('h-[14px] w-[14px] rounded-full border-[2px] border-primary ', {
                [styles.selected]: index === selectedIndex,
              })}
            />
          ))}
        </div>
      </div>
      <div className="block overflow-hidden md:hidden">{Content}</div>
    </>
  );
};

const NewsGroup = ({ group: newGroup, className }: { group: TNews[0]; className: string }) => {
  return (
    <div className={cx('mt-5 min-w-0 max-w-full flex-[0_0_100%]', className)}>
      <div className={'mx-2 grid grid-cols-1 gap-3 md:h-full md:grid-cols-[45%_1fr]'}>
        <div className="md:col-start-1 md:row-span-2 md:row-start-1">
          <BigNews news={newGroup[0]} />
        </div>
        <div className="md:col-start-2 md:row-start-1">
          {newGroup[1] ? <GoodNews news={newGroup[1]} /> : <div className={cx(styles.newsEmpty)} />}
        </div>
        <div className="md:col-start-2 md:row-start-2">
          {newGroup[2] ? <GoodNews news={newGroup[2]} /> : <div className={cx(styles.newsEmpty)} />}
        </div>
        <div className="mt-1 flex items-center justify-center md:hidden">
          <Link
            className="rounded-3xl bg-primary px-3 py-2 text-sm text-white"
            href={`${ROUTES_CLIENT.PRESS_ROOM}`}
          >
            Ver sala de prensa
          </Link>
        </div>
      </div>
    </div>
  );
};

const BigNews = ({ news }: { news: TNews[0][0] }) => {
  return (
    <Link
      href={`${ROUTES_CLIENT.PRESS_ROOM}/${news.seo?.metadata?.[0]?.slug || news.id}`}
      target={news.linkItem?.target ?? '_blank'}
      className="flex h-full flex-1 flex-col rounded-[10px] border border-blueLight bg-white shadow-lg"
    >
      <div className="relative max-h-60 w-full" style={{ aspectRatio: '16/9' }}>
        <ImageWithFallback
          className={'h-44 rounded-t-[10px] object-cover md:h-auto'}
          src={getDownloadURL(news?.image?.id)}
          alt={news?.title}
          fill
          sizes="(min-width: 768px) 31.733vw, (min-width: 420px) 50vw, 100vw"
        />
        <div className="relative left-3 top-2 z-10 inline-flex items-center justify-center gap-2 rounded-full bg-blueLight px-2 py-1 font-workSans text-sm text-primary md:hidden">
          <SvgCalendar width={16} height={16} />
          {news?.publicationDate && dayjs(news.publicationDate).format('LL')}
        </div>
      </div>
      <div className="flex flex-[1_0_0%] flex-col justify-start gap-1 self-stretch p-[20px] md:gap-[12px]">
        <Typography
          variant="body2"
          as={'data'}
          className="hidden overflow-hidden overflow-ellipsis font-normal uppercase text-dimGray md:block"
        >
          {news?.publicationDate && dayjs(news.publicationDate).format('LL')}
        </Typography>

        <Typography
          variant={'h6'}
          className={
            'line-clamp-2 text-base font-semibold leading-tight text-primaryDark underline md:text-lg md:text-black md:no-underline 3xl:text-xl'
          }
          title={news?.title}
        >
          {news?.title}
        </Typography>
        <Typography
          variant="body2"
          className="line-clamp-3 leading-relaxed text-dimGray"
          title={getSummaryText(news?.summary)}
        >
          {getSummaryText(news?.summary)}
        </Typography>
      </div>
    </Link>
  );
};

const GoodNews = ({ news }: { news: TNews[0][0] }) => {
  return (
    <Link
      href={`${ROUTES_CLIENT.PRESS_ROOM}/${news.seo?.metadata?.[0]?.slug || news.id}`}
      target={news.linkItem?.target ?? '_blank'}
      className="flex w-full min-w-0 flex-1 items-stretch self-stretch overflow-hidden rounded-[10px] border border-solid border-blueLight bg-white shadow-md md:h-full"
    >
      <div
        className="relative w-[40%] min-w-0 max-w-[40%] flex-[0_0_40%]"
        style={{ aspectRatio: '4/3' }}
      >
        <ImageWithFallback
          src={getDownloadURL(news?.image?.id)}
          className="h-full w-full rounded-md object-cover md:rounded-none"
          alt={news?.title}
          fill
          sizes="(min-width: 768px) 31.733vw, (min-width: 420px) 50vw, 100vw"
        />
      </div>
      <div
        className={
          'flex min-w-0 flex-1 flex-col justify-start gap-1 overflow-hidden px-4 py-3 md:gap-2'
        }
      >
        <Typography
          variant="body2"
          as={'data'}
          className="hidden overflow-hidden overflow-ellipsis font-normal uppercase text-dimGray md:block"
        >
          {news?.publicationDate && dayjs(news.publicationDate).format('LL')}
        </Typography>

        <Typography
          variant={'h6'}
          className={
            'line-clamp-2 text-sm font-semibold leading-tight text-primaryDark underline md:text-black md:no-underline'
          }
          title={news?.title}
        >
          {news?.title}
        </Typography>
        <Typography
          variant="body2"
          className="leading line-clamp-3 text-[12px] text-gray-600 md:line-clamp-2 md:text-sm md:leading-relaxed"
          title={getSummaryText(news?.summary)}
        >
          {getSummaryText(news?.summary)}
        </Typography>
      </div>
    </Link>
  );
};

export default News;

export function NewsSkeleton() {
  return (
    <section className="bg-concrete pb-[40px] pt-[28px]">
      <div className="inner-extend animate-pulse">
        <div className="mb-2 h-4 w-48 rounded bg-gray-200" />

        <div className="mx-2 grid grid-cols-1 gap-3 md:h-full md:grid-cols-[45%_1fr]">
          <div className="md:col-start-1 md:row-span-2 md:row-start-1">
            <div className="flex h-full flex-1 flex-col rounded-[10px] border border-blueLight bg-white p-0">
              <div className="relative max-h-60 w-full" style={{ aspectRatio: '16/9' }}>
                <div className="h-44 w-full rounded-t-[10px] bg-gray-200" />
              </div>
              <div className="flex flex-col gap-2 p-[20px]">
                <div className="h-3 w-32 rounded bg-gray-200" />
                <div className="h-5 w-full rounded bg-gray-200" />
                <div className="h-4 w-full rounded bg-gray-200" />
              </div>
            </div>
          </div>

          <div className="md:col-start-2 md:row-start-1">
            <div className="flex w-full min-w-0 flex-1 items-stretch self-stretch overflow-hidden rounded-[10px] border border-solid border-blueLight bg-white">
              <div className="relative w-[40%] min-w-0 max-w-[40%]" style={{ aspectRatio: '4/3' }}>
                <div className="h-full w-full bg-gray-200" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-start gap-2 overflow-hidden px-4 py-3">
                <div className="h-3 w-24 rounded bg-gray-200" />
                <div className="h-4 w-full rounded bg-gray-200" />
                <div className="h-3 w-full rounded bg-gray-200" />
              </div>
            </div>
          </div>

          <div className="md:col-start-2 md:row-start-2">
            <div className="flex w-full min-w-0 flex-1 items-stretch self-stretch overflow-hidden rounded-[10px] border border-solid border-blueLight bg-white">
              <div className="relative w-[40%] min-w-0 max-w-[40%]" style={{ aspectRatio: '4/3' }}>
                <div className="h-full w-full bg-gray-200" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-start gap-2 overflow-hidden px-4 py-3">
                <div className="h-3 w-24 rounded bg-gray-200" />
                <div className="h-4 w-full rounded bg-gray-200" />
                <div className="h-3 w-full rounded bg-gray-200" />
              </div>
            </div>
          </div>

          <div className="mt-1 flex items-center justify-center md:hidden">
            <div className="rounded-3xl bg-primary px-3 py-2 text-sm text-white">&nbsp;</div>
          </div>
        </div>
      </div>
    </section>
  );
}
